import React from 'react'
import { createHashRouter, RouteObject, Navigate } from 'react-router-dom'
import ErrorPage from './components/error-page'
import { getDefaultLayout } from './components/layout'
import { getAdminLayout } from './components/admin/dashboard-layout'
import { LoginPage } from './pages/auth/login'
import { ForgotPasswordPage } from './pages/auth/forgot-password'
import { ResetPasswordPage } from './pages/auth/reset-password'
import { CompleteInvitePage } from './pages/auth/complete-invite'
import { UserInfoPage } from './pages/auth/user-info'
import WidgetsPage from './pages/widgets'
import { TOTPEnrollment } from './components/auth/totp-enrollment'
import { ProtectedRoute } from './components/protected-route'
import { AccountSettingsPage } from './pages/account/settings'

// Define public routes that don't require authentication
const publicPaths = [
  '/',
  '/auth/reset-password',
  '/auth/forgot-password',
  '/auth/login',
  '/auth/complete-invite'
];

export const routerObjects: RouteObject[] = [
  {
    path: '/auth/complete-invite',
    element: <CompleteInvitePage />,
    // Add this to ensure hash fragments are handled correctly
    children: [
      {
        path: '*',
        element: <CompleteInvitePage />
      }
    ]
  },
  {
    path: '/auth/reset-password',
    element: <ResetPasswordPage />,
    // Add this to ensure hash fragments are handled correctly
    children: [
      {
        path: '*',
        element: <ResetPasswordPage />
      }
    ]
  },
  {
    path: '/',
    Component: React.lazy(() => import('./pages/home')),
  },
  {
    path: '/admin',
    Component: React.lazy(() => import('./pages/admin')),
    children: [
      {
        path: '',
        Component: React.lazy(() => import('./pages/admin/dashboard')),
      },
      {
        path: 'users',
        Component: React.lazy(() => import('./pages/admin/users')),
      },
      {
        path: 'users/:id',
        Component: React.lazy(() => import('./pages/admin/users/[id]')),
      },
      {
        path: 'profile',
        Component: React.lazy(() => import('./pages/admin/profile')),
      },
      {
        path: 'audit',
        Component: React.lazy(() => import('./pages/admin/audit')),
      },
      {
        path: 'clubs',
        Component: React.lazy(() => import('./pages/admin/clubs')),
      },
      {
        path: 'clubs/:id',
        Component: React.lazy(() => import('./pages/admin/clubs/[id].page')),
      },
      {
        path: 'refdata-import',
        Component: React.lazy(() => import('./pages/admin/refdata-import')),
      },
      {
        path: 'rag-documents',
        Component: React.lazy(() => import('./pages/admin/rag-documents')),
      },
      {
        path: 'cors-debug',
        Component: React.lazy(() => import('./pages/admin/cors-debug')),
      },
      {
        path: 'refresh-club-news',
        Component: React.lazy(() => import('./pages/admin/refresh-club-news')),
      },
      {
        path: 'refresh-player-news',
        Component: React.lazy(() => import('./pages/admin/refresh-player-news')),
      },
      {
        path: 'blocked-domains',
        Component: React.lazy(() => import('./pages/admin/blocked-domains')),
      },
      {
        path: 'sql-query',
        Component: React.lazy(() => import('./pages/admin/sql-query')),
      },
      {
        path: 'automated-kyp',
        Component: React.lazy(() => import('./pages/admin/automated-kyp')),
      },
      {
        path: 'automated-kyp-summary',
        Component: React.lazy(() => import('./pages/admin/automated-kyp-summary')),
      },
      {
        path: 'kyp-search-summary',
        Component: React.lazy(() => import('./pages/admin/kyp-search-summary/list')),
      },
      {
        path: 'kyp-search-summary/:searchId',
        Component: React.lazy(() => import('./pages/admin/kyp-search-summary')),
      },
      {
        path: 'kyp-summary',
        Component: React.lazy(() => import('./pages/admin/kyp-summary')),
      },
      {
        path: 'kyp-v2',
        Component: React.lazy(() => import('./pages/admin/kyp-v2')),
      },
      {
        path: 'monitoring',
        Component: React.lazy(() => import('./pages/admin/monitoring')),
      },
      // Add account settings as a child route of admin
      {
        path: 'account-settings',
        element: (() => {
          console.log('Router: Rendering account settings page within admin layout');
          return <AccountSettingsPage />;
        })(),
      },
      // Redirect /admin/settings to /admin/account-settings
      {
        path: 'settings',
        element: (() => {
          console.log('Router: Redirecting from /admin/settings (child route) to /admin/account-settings');
          return <Navigate to="/admin/account-settings" replace />;
        })(),
      },
    ],
  },
  {
    path: '/login',
    Component: LoginPage,
  },
  {
    path: '/auth/forgot-password',
    Component: ForgotPasswordPage,
  },

  {
    path: '/widgets',
    element: (
      <ProtectedRoute>
        <WidgetsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/user-info',
    Component: UserInfoPage,
  },
  {
    path: '/totp-enrollment',
    Component: TOTPEnrollment,
  },
  {
    path: '/account/settings',
    element: (
      <ProtectedRoute>
        {(() => {
          console.log('Router: Rendering account settings page with default layout');
          return getDefaultLayout(<AccountSettingsPage />);
        })()}
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings',
    element: (() => {
      console.log('Router: Redirecting from /admin/settings to /account/settings');
      return <Navigate to="/account/settings" replace />;
    })(),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]

export function createRouter(): ReturnType<typeof createHashRouter> {
  console.log('[Router] Creating router with public paths:', publicPaths)

  // Create base routes without any wrappers
  const baseRoutes = routerObjects.map(route => {
    // Don't wrap public routes with auth protection
    const isPublicRoute = publicPaths.some(path => route.path?.startsWith(path))
    const requiresAuth = !isPublicRoute && (route.path === '/' || route.path?.startsWith('/admin/'))
    const requiresAdmin = route.path?.startsWith('/admin/')

    console.log('[Router] Processing route:', {
      path: route.path,
      isPublicRoute,
      requiresAuth,
      requiresAdmin,
      hasComponent: !!route.Component,
      hasElement: !!route.element
    })

    let element = route.element
    const Component = route.Component

    if (Component) {
      const LazyComponent = Component
      type LayoutComponent = { getLayout?: typeof getDefaultLayout }
      const getLayout = (LazyComponent as LayoutComponent).getLayout || getDefaultLayout

      console.log('[Router] Setting up component:', {
        path: route.path,
        hasCustomLayout: !!(LazyComponent as LayoutComponent).getLayout,
        willUseDefaultLayout: !(LazyComponent as LayoutComponent).getLayout
      })

      let content = (
        <React.Suspense fallback={<div>Loading...</div>}>
          <LazyComponent />
        </React.Suspense>
      )

      // For admin routes, wrap with admin layout
      if (route.path?.startsWith('/admin')) {
        console.log('[Router] Applying admin layout for path:', route.path)
        content = getAdminLayout(content)
      } else if (!isPublicRoute) {
        console.log('[Router] Applying default layout for path:', route.path)
        content = getLayout(content)
      } else {
        console.log('[Router] No layout applied for public path:', route.path)
      }

      if (requiresAuth) {
        console.log('[Router] Adding auth protection for path:', route.path, { requiresAdmin })
        content = <ProtectedRoute requireAdmin={requiresAdmin}>{content}</ProtectedRoute>
      }

      element = content
    }

    return {
      ...route,
      element: element || route.element,
      Component: null,
      ErrorBoundary: ErrorPage,
    }
  })

  // Log detailed route information
  console.log('[Router] Final routes configuration:', baseRoutes.map(r => ({
    path: r.path,
    hasErrorBoundary: !!r.ErrorBoundary,
    hasChildren: !!r.children?.length,
    isProtected: React.isValidElement(r.element) && r.element.type === ProtectedRoute
  })))

  const router = createHashRouter(baseRoutes)
  console.log('[Router] Router created successfully')
  return router
}
